import { VenueTemplate, BareVenueTemplate } from '../types/venueTemplate';
import { capitalize } from './string';
import { Range, RangeList } from './rangeList';
import {
  DAY_LENGTH,
  timeToMinutes,
  minutesToTimeString,
  TimeStringFormat,
  DEFAULT_TIMESTR_FORMAT,
} from './time';
import { BrandVenueType, BrandCommonTextValues, HourFormat } from '../types/brand';
import { VenueDisposition, VenueHours, VenueTimeDisplayOptions } from '../types/plan';
import { Property } from '../types/property';
import { ResourceNotFoundError } from '../errors';
import { notFalsy, notUndefined, searchById } from './functional';
import { ObjectId } from 'bson';

export const DEFAULT_CLOSED_WORD = 'Closed';
export const DEFAULT_ENDS_LATE_WORD = 'Late';
export const DEFAULT_DECK_WORD = 'Deck';
export const DEFAULT_FWD_WORD = 'Fwd';
export const DEFAULT_MID_WORD = 'Mid';
export const DEFAULT_AFT_WORD = 'Aft';
export const DEFAULT_PRT_WORD = 'Prt';
export const DEFAULT_STB_WORD = 'stb';
export const DEFAULT_24_7_WORD = '24/7';
export const DEFAULT_START_TIME_WORD = 'over ride start time';

/**
 * Returns a string with the venue location, in the form "Aft Stb".
 * If no location is specified, returns an empty string
 *
 */
export function getVenueLocation(
  venue: VenueTemplate | BareVenueTemplate,
  commonTexts: BrandCommonTextValues
) {
  let locationLong = venue.locationLong
    ? capitalize(commonTexts[`venue_location_${venue.locationLong}`])
    : '';
  let locationLat = venue.locationLat
    ? capitalize(commonTexts[`venue_location_${venue.locationLat}`])
    : '';

  return (locationLong + ' ' + locationLat).trim();
}

/**
 * Returns a string with the venue level and location, in the form "1 (Aft Stb)".
 *
 */
export function getVenueDisplayName(
  venue: VenueTemplate | BareVenueTemplate,
  commonTexts: BrandCommonTextValues
): string {
  const location = getVenueLocation(venue, commonTexts);

  return `${venue.level}` + (location.length ? ` (${location})` : '');
}

function getRanges(venueHours: VenueHours[], dayStartMinutes: number) {
  let closedBlocks = venueHours.filter(h => h.status === 'closed');

  let openHours = new RangeList<'free' | 'closed'>(0, DAY_LENGTH, 'free');

  // Add closed blocks (disregard any others)
  closedBlocks.forEach(b => {
    let blockStart = timeToMinutes(b.startTime, dayStartMinutes);
    let blockEnd = timeToMinutes(b.endTime, dayStartMinutes);

    // Adjust for the case when the block ends at the end of day
    // (which would be set to 0 by `timeToMinutes`)
    if (blockEnd === 0) {
      blockEnd = DAY_LENGTH;
    }

    openHours.add(blockStart, blockEnd, 'closed');
  });

  // merge gets rid of duplicates and blocks within blocks
  openHours.merge();

  return openHours.toArray().filter(r => r.value === 'free');
}

export function getBasicTimeStrings(
  venueHours: VenueHours[],
  dayStartMinutes: number,
  hourFormat: HourFormat
) {
  return getRanges(venueHours, dayStartMinutes).map(r => {
    const startMinutes = r.start + dayStartMinutes;
    const endMinutes = r.end + dayStartMinutes;

    return `${minutesToTimeString(
      startMinutes,
      hourFormat,
      DEFAULT_TIMESTR_FORMAT
    )} - ${minutesToTimeString(endMinutes, hourFormat, DEFAULT_TIMESTR_FORMAT)}`;
  });
}

export interface VenueTimePair {
  minutes: number;
  timeString: string;
  displayOptions?: VenueTimeDisplayOptions;
}

function getTimePairs(
  ranges: Range<'closed' | 'free'>[],
  dayStartMinutes: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  commonTexts: BrandCommonTextValues,
  displayOptions?: VenueTimeDisplayOptions,
  keepTimePairs?: boolean
  // keepTimePairs params is used to prevent removing the elements
  // from the results with the tradeoff of having to calculate
  // later the timeString value
  // This field was added to keep the Export API response
  // without breaking changes
): VenueTimePair[][] {
  const { venue_closed, ends_late, override_start_time, twenty_four_seven } = commonTexts;

  let timePairs = ranges.map(r => {
    const startMinutes = r.start + dayStartMinutes;
    const endMinutes = r.end + dayStartMinutes;

    return [
      {
        minutes: startMinutes,
        timeString: minutesToTimeString(startMinutes, hourFormat, timeFormat),
        displayOptions,
      },
      {
        minutes: endMinutes,
        timeString: minutesToTimeString(endMinutes, hourFormat, timeFormat),
        displayOptions,
      },
    ];
  });

  switch (displayOptions) {
    case 'ends-late': {
      if (timePairs.length > 0) {
        timePairs[timePairs.length - 1][1].timeString = ends_late;
      }
      break;
    }
    case 'do-not-show-finish-time': {
      // remove end minutes
      if (!keepTimePairs) {
        timePairs = timePairs.map(tp => [tp[0]]);
      }
      break;
    }
    case 'show-as-24/7': {
      const timeString = twenty_four_seven;

      if (keepTimePairs) {
        timePairs = timePairs.map(tp => {
          return [
            { ...tp[0], timeString },
            { ...tp[1], timeString },
          ];
        });
      } else {
        // remove end minutes + set start to 24h + only one time pair
        timePairs = [[{ minutes: dayStartMinutes, timeString, displayOptions }]];
      }
      break;
    }
    case 'override-start-time': {
      if (timePairs[0]) {
        timePairs[0][0].timeString = override_start_time;
        break;
      }
    }
  }

  // Special case when there are no open hours. Instead of returning an
  // empty array, we return the word "Closed"
  if (timePairs.length === 0) {
    timePairs = [[{ minutes: dayStartMinutes, timeString: venue_closed, displayOptions }]];
  }

  return timePairs;
}

/**
 * Given a venue's `VenueDisposition` information, return a list of
 * strings with the availability times. This only accounts for venue
 * hours that are not closed.
 *
 */
export function getVenueAvailabilityStrings(
  venueHours: VenueHours[],
  dayStartMinutes: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  commonTexts: BrandCommonTextValues,
  displayOptions?: VenueTimeDisplayOptions
): string[] {
  const availabilities = getVenueAvailabilityTimePairs(
    venueHours,
    dayStartMinutes,
    hourFormat,
    timeFormat,
    commonTexts,
    displayOptions
  );

  return availabilities.map(pair =>
    pair
      .map(a => a.timeString)
      .filter(notFalsy)
      .join(' - ')
  );
}

export function getVenueAvailabilityTimePairs(
  venueHours: VenueHours[],
  dayStartMinutes: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  commonTexts: BrandCommonTextValues,
  displayOptions?: VenueTimeDisplayOptions,
  keepTimePairs?: boolean
): VenueTimePair[][] {
  const ranges = getRanges(venueHours, dayStartMinutes);
  const timePairs = getTimePairs(
    ranges,
    dayStartMinutes,
    hourFormat,
    timeFormat,
    commonTexts,
    displayOptions,
    keepTimePairs
  );

  return timePairs;
}

export function populateVenueTemplate(
  bareVenueTemplate: BareVenueTemplate,
  property: Property
): VenueTemplate {
  let types: BrandVenueType[] = [];
  bareVenueTemplate.types.forEach(typeId => {
    let venueType = searchById(typeId, property.brand.venueTypes);
    if (!venueType) throw new ResourceNotFoundError(`Cannot find referenced venue type ${typeId}`);
    types.push(venueType);
  });

  return {
    ...bareVenueTemplate,
    types,
    property,
  };
}

export function getVenueStatus(
  dayStartMinutes: number,
  dayMinutesPassed: number,
  availabilities: VenueTimePair[][]
) {
  let isOpen = false;
  let status = 'Open';

  availabilities.forEach(avl => {
    let start: VenueTimePair = { ...avl[0] };
    let end: VenueTimePair = {
      minutes: 0,
      timeString: '',
      ...((avl[1] as VenueTimePair | undefined) || {}),
    };

    if (start.displayOptions === 'show-as-24/7') {
      status = 'Open';
      isOpen = true;
      return;
    }

    if (['do-not-show-finish-time', 'ends-late'].includes(start.displayOptions || '')) {
      end.minutes = 24 * 60 + dayStartMinutes;
    }

    if (dayMinutesPassed >= start.minutes && dayMinutesPassed < end.minutes) {
      status = 'Open';
      isOpen = true;
    }

    const startDelta = start.minutes - dayMinutesPassed;
    const endDelta = end.minutes - dayMinutesPassed;

    if (status === 'Closed' && startDelta > 0 && startDelta < 60) {
      status = 'Opening Soon';
    }

    if (status === 'Open' && endDelta > 0 && endDelta < 60) {
      status = 'Closing Soon';
    }
  });

  return { isOpen, status };
}

/**
 * Create an empty venue disposition for a venue
 */
export function makeEmptyVenueDisposition(
  venueTemplateId: string,
  venueTemplate?: VenueTemplate
): VenueDisposition {
  return {
    _id: new ObjectId().toHexString(),
    template: venueTemplateId,
    hours: [],
    callToActions: { syncWithTemplate: true, values: venueTemplate?.callToActions || [] },
    mealPeriods: [],
    displayOptions: {},
  };
}

export type VENUE_STRING_STYLE = 'name-only' | 'no-label' | 'full';

export function getVenueDisplay(
  venue: VenueDisposition,
  venTemplate: VenueTemplate | BareVenueTemplate,
  commonTexts: BrandCommonTextValues,
  venueStringType?: VENUE_STRING_STYLE
) {
  const label = commonTexts.deck;

  const name = venue.displayOptions?.displayName || venTemplate.name;
  const deck =
    venue.displayOptions?.deckDisplayName || getVenueDisplayName(venTemplate, commonTexts);

  const location = [venueStringType !== 'no-label' ? label : undefined, deck]
    .filter(notUndefined)
    .join(' ');

  return {
    name,
    location,
    label,
    venueString: [name, venueStringType !== 'name-only' ? location : undefined]
      .filter(notUndefined)
      .join(', '),
  };
}
